import React, { FC, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import { CustomTab } from '../../components/commons/CustomTab';
import './AccountSettings.scss';
import { accountSettingPages, accountSettingPagesWithDocuments } from './accountSettingPages';
import { CustomSelect } from '../../components/commons/CustomSelect';
import { useAppSelector } from '../../hooks/redux';
import { AccountSettingRouteNames, GlobalRouteNames } from '../../models/Common';

export const AccountSetting: FC = () => {
  const { path } = useRouteMatch();
  const [tabValue, setTabValue] = useState<number>(0);
  const history = useHistory();
  const { t } = useTranslation();
  const { settings } = useAppSelector((state) => state.appReducer);
  const { userInfoState, isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const isRegularPaymentAvailable = userInfoState.Permissions.Payments.RegularPayment;
  const [accountPages, setAccountPages] = useState(accountSettingPagesWithDocuments);

  const updateTabValueAndChangeRoute = (pathHistory) => {
    const lastUrlSegmentPage = pathHistory.split('/')[2];
    const currentPageLastUrlSegment = lastUrlSegmentPage ? `/${lastUrlSegmentPage}` : '/';
    const currentPageIndex = accountPages.findIndex((el) => el.routePath === currentPageLastUrlSegment);
    setTabValue(currentPageIndex);
    return currentPageLastUrlSegment;
  };

  useEffect(() => {
    if (history.action === 'POP') {
      updateTabValueAndChangeRoute(history.location.pathname);
    }
    updateTabValueAndChangeRoute(history.location.pathname);
  }, [history.location.pathname, accountPages.length]);

  useEffect(() => {
    if (typeof settings?.feature?.digitalDocuments !== 'undefined') {
      const pages = settings?.feature?.digitalDocuments ? accountSettingPagesWithDocuments : accountSettingPages;
      setAccountPages(
        pages.filter((p) => {
          if (p.routePath === AccountSettingRouteNames.MultiAccountPage) {
            return settings?.feature?.multiaccount;
          }
          return true;
        }),
      );
    }
  }, [settings?.feature?.digitalDocuments]);

  const handleSetTabValue = (
    event: React.SyntheticEvent | SelectChangeEvent<string | number>,
    newValue: number | string,
  ) => {
    history.push(`${path}${accountPages[+newValue].routePath}`);
  };

  const handleTabClick = (index: number | string) => {
    if (tabValue === index) {
      history.replace(`${path}${accountPages[+index].routePath}`);
    }
  };

  return (
    <div className="account-setting-page page">
      <div className="account-setting-page__container container">
        <h1 className="account-setting-page__title">{t('buttons.accountSettings')}</h1>
        <div
          className={`account-setting-page__card-tab-container ${
            isUserInfoDownloaded ? 'skeleton--disabled' : 'skeleton'
          }`}
        >
          <CustomTab
            tabValue={tabValue}
            setTabValue={handleSetTabValue}
            clickTabValue={handleTabClick}
            orientation="vertical"
            tabs={accountPages.map((el, index) => {
              if (t(el.label) === t('accountSettings.multiAccount')) {
                return { label: t('multiAccount.brizAccounts'), id: index };
              }
              return { label: t(el.label), id: index };
            })}
            isAutoPayAvailable={!isRegularPaymentAvailable}
          />
          <CustomSelect
            value={tabValue}
            setValue={handleSetTabValue}
            clickTabValue={handleTabClick}
            items={accountPages.map((el, index) => {
              if (t(el.label) === t('accountSettings.multiAccount')) {
                return { label: t('multiAccount.brizAccounts'), id: index };
              }
              return { label: t(el.label), id: index };
            })}
            isAutoPayAvailable={!isRegularPaymentAvailable}
          />
          <Switch>
            {accountPages.map((page) => {
              if (
                isUserInfoDownloaded &&
                page.routePath === AccountSettingRouteNames.AutoReplenishmentPage &&
                !isRegularPaymentAvailable
              ) {
                return (
                  <Route exact={!!page.isExact} path={path.concat(page.routePath)} key={page.routePath}>
                    <Redirect to={GlobalRouteNames.AccessDenied} />
                  </Route>
                );
              }
              return (
                <Route exact={!!page.isExact} path={path.concat(page.routePath)} key={page.routePath}>
                  <div className="account-setting-page__card-tab-container__card card card__primary card__primary_full-mobile">
                    {page.component}
                  </div>
                </Route>
              );
            })}
          </Switch>
        </div>
      </div>
    </div>
  );
};
